import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Catches failures from the lazily-loaded route chunks.
 *
 * Without this, a single failed dynamic import takes down the whole app with a
 * blank screen. The common cause is not a code bug: when a new version is
 * deployed, the hashed chunk filenames change, so an already-open tab holding
 * the previous index.html asks for a chunk that no longer exists. A reload
 * fetches the current index.html and resolves it, which is why the recovery
 * action is a hard reload rather than a re-render.
 */
export class RouteErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Route failed to load:", error, info.componentStack);
  }

  private isChunkLoadError(error: Error) {
    return /Loading chunk|Failed to fetch dynamically imported module|Importing a module script failed/i.test(
      `${error.name} ${error.message}`,
    );
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const stale = this.isChunkLoadError(error);

    return (
      <div
        className="min-h-screen flex items-center justify-center px-6 bg-navy-900"
        data-testid="route-error"
      >
        <div className="max-w-md text-center">
          <h1 className="text-white text-2xl font-bold">
            {stale ? "This page needs a refresh" : "Something went wrong"}
          </h1>
          <p className="text-gray-400 mt-3 leading-relaxed">
            {stale
              ? "The app was updated while this tab was open, so part of it could not be loaded. Reloading will pick up the latest version."
              : "This page failed to load. Reloading usually clears it."}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="mt-6 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-6 py-3 font-semibold transition-colors"
            data-testid="button-route-error-reload"
          >
            Reload page
          </button>
        </div>
      </div>
    );
  }
}
